import {Controller, useFieldArray, useFormContext} from "react-hook-form";
import {usePricingCurrencies} from "@/features/currencies";
import {Button, Col, message, Row, Select, Space, Tooltip} from "antd";
import {SessionCurrenciesDefaultCallCurrencyFormField} from "./default-currency-form-field";

import {DeleteOutlined, PlusCircleFilled} from "@ant-design/icons";
import {CustomInputComponent} from "../../../../../input/input-component";
import {useCurrencyOptions} from "../hooks/currency";
import {SessionPricingFormFieldsType} from "../types";
import {SessionCurrenciesFormFieldProps} from "./types";
import {AutomaticCurrencyConversionNotice} from "../../../../../../components/shared/automatic-currency-conversion";

export function SessionCurrenciesFormField({
  id,
  label,
  placeholder,
}: SessionCurrenciesFormFieldProps) {
  const pricingCurrencies = usePricingCurrencies();
  const {
    control,
    watch,
    formState: {errors},
    setValue,
  } = useFormContext<SessionPricingFormFieldsType>();

  const {
    fields: currencyFields,
    append: appendCurrency,
    remove: removeCurrency,
  } = useFieldArray({
    control,
    name: "currencies",
  });

  const currencyArrayValue = watch("currencies");
  const defaultCallCurrency = watch("callPriceCurrency");

  const {
    currencyOptions,
    handleCurrencySelectChange,
    removeCurrency: handleRemoveCurrency,
    addNewCurrency,
  } = useCurrencyOptions(
    currencyArrayValue,
    defaultCallCurrency,
    appendCurrency,
    removeCurrency
  );

  const canAddCurrencies =
    watch("callPriceType") === "paid" &&
    pricingCurrencies.connectedBankStatus.isBankConnected;

  function handleDeleteCurrency(currency: string, currencyIndex: number) {
    handleRemoveCurrency(currencyIndex);
  }
  return (
    <div>
      {canAddCurrencies && (
        <Space direction="vertical" size={24} style={{display: "flex"}}>
          <Row gutter={[10, 24]}>
            <Col span={24} sm={12}>
              <SessionCurrenciesDefaultCallCurrencyFormField
                currencyOptions={currencyOptions}
              />
            </Col>
            {currencyFields.map((item, index) => (
              <Col key={item.id} span={24} sm={12}>
                <div style={{display: "flex", gap: "1.6rem"}}>
                  <div style={{flex: "1"}}>
                    <CustomInputComponent
                      id="callPrice"
                      name={`currencies.${index}.callPrice`}
                      type="text"
                      label={label}
                      placeholder={placeholder ?? ""}
                      error={
                        errors.currencies?.[index]?.callPriceCurrency
                          ? errors.currencies?.[index]?.callPriceCurrency?.message
                          : errors.currencies?.[index]?.callPrice?.message
                      }
                      control={control}
                      addonBefore={
                        <Controller
                          control={control}
                          name={`currencies.${index}.callPriceCurrency`}
                          render={({field: {onChange, value, ref}}) => (
                            <>
                              <Select
                                onChange={(val) =>
                                  handleCurrencySelectChange(val, onChange)
                                }
                                value={value}
                                ref={ref}
                                size="large"
                                status={
                                  errors.currencies?.[index]?.callPriceCurrency?.message
                                    ? "error"
                                    : ""
                                }
                                showSearch={false}
                                notFoundContent={null}
                                placeholder={""}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  (option!.children as unknown as string)
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              >
                                {currencyOptions.map(
                                  (option, optionIdx: number | string) => (
                                    <Select.Option key={optionIdx} value={option}>
                                      {option}
                                    </Select.Option>
                                  )
                                )}
                              </Select>
                              {errors.currencies?.[index]?.callPriceCurrency?.message && (
                                <div role="alert" className="ant-form-item-explain-error">
                                  {errors.currencies?.[index]?.callPriceCurrency?.message}
                                </div>
                              )}
                            </>
                          )}
                        />
                      }
                    />
                  </div>
                  <div style={{marginTop: "auto"}}>
                    <Tooltip
                      title={
                        currencyFields.length === 0
                          ? "At least one currency is required"
                          : null
                      }
                    >
                      <Button
                        onClick={() =>
                          handleDeleteCurrency(item.callPriceCurrency, index)
                        }
                        type="link"
                        shape="circle"
                        icon={<DeleteOutlined />}
                        size="small"
                        disabled={currencyFields.length === 0}
                      />
                    </Tooltip>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          {watch("callPriceType") === "paid" && (
            <div>
              <Button
                icon={<PlusCircleFilled />}
                onClick={() => addNewCurrency()}
                size="small"
              >
                Add currency
              </Button>
              <div style={{marginTop: "2.4rem"}}>
                <AutomaticCurrencyConversionNotice sourceCurrency={defaultCallCurrency} />
              </div>
            </div>
          )}
        </Space>
      )}
    </div>
  );
}
